import { Button, TextInput } from "@mantine/core";
import {
  IconLayoutGridFilled,
  IconList,
  IconPlus,
  IconSearch,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import InventoryCreatedCard from "@/components/InventoryCreatedCard";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table/interface";
import { orpcClient } from "@/lib/orpc-client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function InventoryCreatedManagement() {
  const { t } = useTranslation();
  // list or grid
  const [layout, setLayout] = useState("list");
  const [productData, setProductData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const navigate = useNavigate();

  const handleAddProduct = () => {
    navigate("/hrMainPage/addInventoryCreated");
  };

  const handleLayoutChange = (layout) => {
    setLayout(layout);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;

    const filtered = productData.filter((item) =>
      item?.name?.toLowerCase().startsWith(value.trim().toLowerCase())
    );

    if (value === "") {
      setFilteredData(productData);
    } else {
      setFilteredData(filtered);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [result, employees] = await Promise.all([
          orpcClient.stockReceipt.list({}),
          orpcClient.employee.list({}),
        ]);

        const employeeMap = Object.fromEntries(
          (employees || []).map((e) => [e.id, e])
        );

        const normalReceipts =
          result?.filter((item) => item.status === "normal") || [];

        const enriched = normalReceipts.map((item) => ({
          ...item,
          warehouseStaff: item.warehouseStaffId
            ? employeeMap[item.warehouseStaffId] || null
            : null,
        }));

        setProductData(enriched);
        setFilteredData(enriched);
      } catch (error) {
        console.error("Error fetching stock receipts data:", error);
      }
    };

    fetchData();
  }, []);

  const formatDate = (value: any) => {
    if (!value) return "";
    const date = new Date(value);
    return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}`;
  };

  const columns: ColumnsType<any> = [
    {
      title: t("warehouse.name") || "名稱",
      dataIndex: "name",
      key: "name",
      fixed: "left" as const,
      sorter: (a: any, b: any) => (a?.name || "").localeCompare(b?.name || ""),
      render: (text: string) => text || "",
    },
    {
      title: t("warehouse.deliveryPerson") || "送貨人員",
      dataIndex: "deliveryPerson",
      key: "deliveryPerson",
      sorter: (a: any, b: any) =>
        (a?.deliveryPerson || "").localeCompare(b?.deliveryPerson || ""),
      render: (text: string) => text || "",
    },
    {
      title: t("warehouse.licensePlateNumber") || "送貨車牌",
      dataIndex: "licensePlateNumber",
      key: "licensePlateNumber",
      sorter: (a: any, b: any) =>
        (a?.licensePlateNumber || "").localeCompare(b?.licensePlateNumber || ""),
      render: (text: string) => text || "",
    },
    {
      title: t("warehouse.inboundStaff") || "入庫人員",
      dataIndex: "warehouseStaff",
      key: "warehouseStaff",
      sorter: (a: any, b: any) =>
        (a?.warehouseStaff?.name || "").localeCompare(
          b?.warehouseStaff?.name || ""
        ),
      render: (staff: any) => staff?.name || "",
    },
    {
      title: t("warehouse.createdTime") || "建立時間",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a: any, b: any) =>
        new Date(a?.createdAt || 0).getTime() -
        new Date(b?.createdAt || 0).getTime(),
      render: (value: any) => formatDate(value),
    },
  ];

  const toggleButton = (
    value: string,
    Icon: React.ComponentType<{ size?: number | string }>
  ) => (
    <button
      type="button"
      onClick={() => handleLayoutChange(value)}
      className={`flex h-9 w-9 items-center justify-center rounded-lg border ${
        layout === value
          ? "border-blue-200 bg-blue-50 text-blue-600"
          : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
      }`}
    >
      <Icon size={18} />
    </button>
  );

  return !productData || productData?.length === 0 ? (
    <div className="flex min-h-screen items-center justify-center p-8">
      <div className="w-[487px] rounded-xl bg-white px-16 py-20 shadow-sm">
        <div className="text-center" style={{ color: "#6F6F6F" }}>
          {t("warehouse.noStockReceiptData") ||
            "您尚未建立入庫單資料！你可以試試："}
        </div>
        <div className="flex text-center">
          <Button
            variant="outline"
            color="gray"
            style={{ width: "100%", margin: "1rem" }}
            radius="md"
            onClick={handleAddProduct}
          >
            {t("hrMain.manualAdd") || "手動新增"}
          </Button>
        </div>
      </div>
    </div>
  ) : (
    <div className="min-h-screen p-8">
      <div className="mx-auto max-w-6xl space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
              {t("warehouse.stockReceiptManagement") || "入庫單管理"}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              {t("warehouse.stockReceiptList") || "入庫單清單"}
            </div>
          </div>
          <Button
            variant="filled"
            color="gray"
            radius="md"
            leftSection={<IconPlus size={16} />}
            onClick={handleAddProduct}
          >
            {t("warehouse.createStockReceipt") || "建立入庫單"}
          </Button>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-6 py-4">
            <TextInput
              className="w-full max-w-md"
              leftSection={<IconSearch size={16} />}
              placeholder={t("warehouse.stockReceiptName") || "入庫單名稱"}
              onChange={handleInputChange}
            />
            <div className="flex gap-2">
              {toggleButton("list", IconList)}
              {toggleButton("grid", IconLayoutGridFilled)}
            </div>
          </div>

          {layout === "list" ? (
            <div className="p-4">
              <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="id"
                onRow={(record: any) => ({
                  onClick: () =>
                    navigate(`/hrMainPage/inventoryCreated/${record.id}`),
                  style: { cursor: "pointer" },
                })}
                scroll={{ x: "max-content", y: 600 }}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "25", "50", "100"],
                  showQuickJumper: false,
                }}
                bordered
                sticky
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredData?.map((data) => {
                return (
                  <InventoryCreatedCard
                    key={data?.id}
                    props={data}
                    onClick={() =>
                      navigate(`/hrMainPage/inventoryCreated/${data?.id}`)
                    }
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InventoryCreatedManagement;
